/**
 * Client-side batching for the Postcrossing import.
 *
 * Every card in a batch costs a remote image fetch plus a disk write, so a few hundred cards
 * in one request can outlive the 5-minute write timeout on the Go server, the matching
 * nginx proxy timeout, and whatever the browser tolerates. When that happened the server
 * finished the import but the UI reported failure. Sending the batch as a series of small
 * requests keeps each one short, whatever the total size.
 */

/** One card as the Postcrossing scraper emits it; the server validates every field. */
export interface ImportCard {
  id?: string;
  sender?: string;
  country?: string;
  sent?: string;
  received?: string;
  imageUrl?: string;
}

/** Running counts across batches, in the server's terms: saved now, or already there. */
export interface ImportTotals {
  importedCount: number;
  skippedCount: number;
}

/** The server's reply to one batch. `error` is set on a rejected batch. */
export interface ImportBatchResult extends ImportTotals {
  error?: string;
}

/**
 * The shortest timeout on the route: nginx's proxy_read_timeout for /api/import/ (the Go
 * server's WriteTimeout is the same). A request that runs longer fails even if the server
 * finishes the work.
 */
const ROUTE_TIMEOUT_SECONDS = 300;

/**
 * How long one card is allowed to take. A Postcrossing image normally downloads in about
 * a second; ten is a slow day. The true worst case is the server's 60-second fetch
 * timeout per card, but no batch size above three could fit that, and a batch that slow
 * means Postcrossing is down and should fail.
 */
const CARD_ALLOWANCE_SECONDS = 10;

/** Cards per request, sized so a slow batch still finishes inside the route timeout. */
export const IMPORT_BATCH_SIZE = Math.floor((ROUTE_TIMEOUT_SECONDS - 50) / CARD_ALLOWANCE_SECONDS);

/**
 * A batch failed. The totals are what was saved before the failure: every earlier batch,
 * plus the cards the failed batch got through before the bad one — the server rejects a
 * batch mid-loop and reports those in its error reply. It keeps all of those cards, so
 * the page must not describe the import as a clean failure.
 *
 * When the reply never arrived (`replyLost`) there was nothing to fold in, and the server
 * may have gone on saving cards after the connection dropped. The totals are then a floor,
 * not an exact count.
 */
export class ImportBatchError extends Error implements ImportTotals {
  importedCount: number;
  skippedCount: number;
  replyLost: boolean;

  constructor(message: string, totals: ImportTotals, options: { replyLost?: boolean } = {}) {
    super(message);
    this.name = "ImportBatchError";
    this.importedCount = totals.importedCount;
    this.skippedCount = totals.skippedCount;
    this.replyLost = options.replyLost ?? false;
  }
}

/**
 * Reads the pasted `{ "cards": [...] }` document. Parsing here, rather than pasting the raw
 * text through to the server, is what makes splitting it possible.
 */
export function parseImportCards(json: string): ImportCard[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new Error("Could not parse the submitted JSON.");
  }
  const cards =
    parsed !== null && typeof parsed === "object" && "cards" in parsed
      ? (parsed as { cards: unknown }).cards
      : undefined;
  if (!Array.isArray(cards)) {
    throw new Error('The JSON must be an object with a "cards" list.');
  }
  return cards as ImportCard[];
}

/** Splits the list into consecutive batches of at most `size`, keeping the order. */
export function chunkCards<T>(cards: T[], size: number): T[][] {
  if (size < 1) throw new Error("Batch size must be at least 1.");
  const batches: T[][] = [];
  for (let start = 0; start < cards.length; start += size) {
    batches.push(cards.slice(start, start + size));
  }
  return batches;
}

/** Posts one batch. Resolves with the server's reply; `ok` is false for any error status. */
export type PostBatch = (cards: ImportCard[]) => Promise<{ ok: boolean; result: ImportBatchResult }>;

/** Posts one batch to the import endpoint. Any HTTP status other than 2xx is `ok: false`. */
export const postImportBatch: PostBatch = async (cards) => {
  const response = await fetch("/api/import/postcrossing", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ cards }),
  });
  const result = (await response.json()) as Partial<ImportBatchResult>;
  return {
    ok: response.ok,
    result: {
      importedCount: result.importedCount ?? 0,
      skippedCount: result.skippedCount ?? 0,
      error: result.error,
    },
  };
};

/**
 * Imports the cards one batch at a time, in order, and adds up the counts. `onProgress`
 * fires before each batch with what has been saved so far and how many cards remain.
 *
 * Stops at the first failed batch: the server rejects a batch on the first bad card, so
 * later batches would be no more likely to succeed, and the owner should read the error
 * before retrying. Cards already saved stay saved; the thrown error reports all of them,
 * including the ones the failed batch saved before the bad card.
 */
export async function importInBatches(
  cards: ImportCard[],
  post: PostBatch,
  options: { batchSize?: number; onProgress?: (totals: ImportTotals, remaining: number) => void } = {},
): Promise<ImportTotals> {
  const batchSize = options.batchSize ?? IMPORT_BATCH_SIZE;
  const totals: ImportTotals = { importedCount: 0, skippedCount: 0 };
  let remaining = cards.length;

  for (const batch of chunkCards(cards, batchSize)) {
    options.onProgress?.({ ...totals }, remaining);
    let ok: boolean;
    let result: ImportBatchResult;
    try {
      ({ ok, result } = await post(batch));
    } catch {
      // No reply, so no partial counts: the server may still have saved part of this batch.
      throw new ImportBatchError("Could not import cards.", totals, { replyLost: true });
    }
    // An error reply carries the cards saved before the one it rejected; they count too.
    totals.importedCount += result.importedCount ?? 0;
    totals.skippedCount += result.skippedCount ?? 0;
    if (!ok) {
      throw new ImportBatchError(result.error || "Could not import cards.", totals);
    }
    remaining -= batch.length;
  }
  return totals;
}

/** One sentence for the running or final counts. */
export function describeTotals(totals: ImportTotals): string {
  return `Imported ${totals.importedCount} cards; skipped ${totals.skippedCount} already saved`;
}

/**
 * The status line for a failed import. When cards were saved before the failure, says
 * so, so a retry of the same paste is expected to skip them.
 *
 * A server rejection reports an exact count, because its reply includes the cards saved
 * before the bad one. A lost reply does not, so that sentence is hedged: the failed batch
 * may have saved more than the count shows.
 */
export function describeFailure(error: unknown, fallback = "Could not import cards."): string {
  if (!(error instanceof ImportBatchError)) {
    return error instanceof Error ? error.message : fallback;
  }
  const saved = error.importedCount + error.skippedCount > 0;
  if (error.replyLost) {
    const before = saved ? ` Before that batch: ${describeTotals(error).toLowerCase()}.` : "";
    const caveat = saved
      ? "That batch may have saved some cards too; a retry will skip anything already saved."
      : "That batch may still have saved some cards; a retry will skip anything already saved.";
    return `${error.message}${before} ${caveat}`;
  }
  if (saved) {
    return `${error.message} Before that: ${describeTotals(error).toLowerCase()}.`;
  }
  return error.message;
}
